import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { CheckRounded, ChevronLeftRounded, ChevronRightRounded, MicNoneRounded, PodcastsRounded } from '@mui/icons-material';

// The queue belongs to the same scrolling row as the related topics. Keep its
// source identity separate from the current clip's artwork and topic tags.
export default function PreviewTopicRow({ presentation, topics, previewId, isActive,
    queueTriggerRef, onOpenQueue, onTopicClick }) {
    const scrollerRef = useRef(null);
    const leftArrowRef = useRef(null);
    const rightArrowRef = useRef(null);
    const pointerFocusTarget = useRef(null);
    const restoringArrowFocus = useRef(false);
    const [edges, setEdges] = useState({ left: false, right: false });
    const [failedImage, setFailedImage] = useState(null);
    const { title, chipLabel, chipImageUrl, chipImageShape } = presentation;
    const contentKey = [title, chipLabel, chipImageUrl, ...topics.map(topic => topic.id)].join('|');

    const measure = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const left = scroller.scrollLeft > 2;
        const right = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 2;
        // An arrow disappears at the edge; retain the keyboard user's place
        // in the row. Suppress our focus handler's scrolling as well as the
        // browser's so an oversized queue stays at the edge just reached.
        restoringArrowFocus.current = true;
        if (!left && leftArrowRef.current === document.activeElement) scroller.firstElementChild?.focus({ preventScroll: true });
        if (!right && rightArrowRef.current === document.activeElement) scroller.lastElementChild?.focus({ preventScroll: true });
        restoringArrowFocus.current = false;
        setEdges(previous => previous.left === left && previous.right === right ? previous : { left, right });
    }, []);

    useLayoutEffect(() => {
        if (isActive && scrollerRef.current) scrollerRef.current.scrollLeft = 0;
        measure();
    }, [previewId, isActive, title, measure]);

    useLayoutEffect(() => {
        measure();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        if (scrollerRef.current) observer?.observe(scrollerRef.current);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [contentKey, measure]);

    const scrollTo = (left, smooth = true) => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const offset = Math.max(0, Math.min(left, scroller.scrollWidth - scroller.clientWidth));
        if (scroller.scrollTo) {
            scroller.scrollTo({ left: offset,
                behavior: smooth && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
        } else {
            scroller.scrollLeft = offset;
        }
        measure();
    };

    const scrollTopics = direction => {
        const scroller = scrollerRef.current;
        const start = scroller.scrollLeft;
        const viewport = scroller.getBoundingClientRect();
        const pageWidth = Math.max(1, viewport.width - 88);
        const boxes = Array.from(scroller.children).map(child => child.getBoundingClientRect());
        if (direction > 0) {
            // Reveal the first obscured chip, including one partially behind
            // the edge fade. Jumping by left edges can skip long topic names.
            const next = boxes.find(box => box.right > viewport.right - 44 + 1);
            // An uncapped queue label can exceed the viewport. Page through
            // its text before moving on to the next chip.
            if (next && next.width > pageWidth && next.left <= viewport.left + 44) {
                scrollTo(Math.min(start + pageWidth, start + next.right - viewport.right + 44));
                return;
            }
            scrollTo(next ? Math.max(start + 1, start + next.left - viewport.left - 44) : scroller.scrollWidth);
        } else {
            const previous = boxes.reverse().find(box => box.left < viewport.left + 44 - 1);
            if (previous && previous.width > pageWidth && previous.right >= viewport.right - 44) {
                scrollTo(Math.max(start - pageWidth, start + previous.left - viewport.left - 44));
                return;
            }
            scrollTo(previous ? Math.min(start - 1, start + previous.right - viewport.right + 44) : 0);
        }
    };

    const revealFocusedChip = event => {
        const focusedByPointer = pointerFocusTarget.current === event.target;
        pointerFocusTarget.current = null;
        // Moving a button between pointer down and up can cancel its click.
        if (focusedByPointer || restoringArrowFocus.current) return;
        const scroller = scrollerRef.current;
        const viewport = scroller.getBoundingClientRect();
        const chip = event.target.getBoundingClientRect();
        if (!viewport.width) return;
        if (chip.left < viewport.left + 44 || chip.right > viewport.right - 44) {
            scrollTo(scroller.scrollLeft + chip.left - viewport.left - 44, false);
        }
    };

    return <div className='previewTopicsViewport'>
        <div ref={scrollerRef} className='previewCardTopics hideScrollbar' role='group' aria-label='Queue and related topics'
            onMouseDownCapture={event => { pointerFocusTarget.current = event.target.closest('button'); }}
            onMouseUpCapture={() => { pointerFocusTarget.current = null; }}
            onScroll={measure} onFocus={revealFocusedChip}>
            <button ref={queueTriggerRef} type='button' className='previewTopicButton previewQueueChip'
                aria-current='true' aria-haspopup='dialog' aria-label={`View queue: ${title}`}
                title={`View queue: ${title}`} onClick={onOpenQueue}>
                <span className='previewTopicPill previewTopicPillCurrent'>
                    <CheckRounded aria-hidden='true' />
                    {chipImageShape ? <span className={`previewQueueImage previewQueueImage-${chipImageShape}`}>
                        {chipImageUrl && failedImage !== chipImageUrl
                            ? <img src={chipImageUrl} alt='' onError={() => setFailedImage(chipImageUrl)} />
                            : chipImageShape === 'circle' ? <MicNoneRounded aria-hidden='true' /> : <PodcastsRounded aria-hidden='true' />}
                    </span> : <span className='previewQueueChipLabel'>{chipLabel}</span>}
                </span>
            </button>
            {topics.map(topic => <button type='button' key={topic.id} className='previewTopicButton' title={topic.id}
                onClick={onTopicClick(topic)}>
                <span className='previewTopicPill'><span className='previewTopicLabel'>{topic.id}</span></span>
            </button>)}
        </div>
        {edges.left && <div className='previewTopicEdge previewTopicEdgeLeft'>
            <button ref={leftArrowRef} type='button' className='previewTopicScrollButton' aria-label='Scroll topics left'
                title='Earlier topics' onClick={() => scrollTopics(-1)}><ChevronLeftRounded aria-hidden='true' /></button>
        </div>}
        {edges.right && <div className='previewTopicEdge previewTopicEdgeRight'>
            <button ref={rightArrowRef} type='button' className='previewTopicScrollButton' aria-label='Scroll topics right'
                title='More topics' onClick={() => scrollTopics(1)}><ChevronRightRounded aria-hidden='true' /></button>
        </div>}
    </div>;
}
